import { useExperiment } from "@hopper-b2b/experiments";
import { Currency } from "@hopper-b2b/types";
import {
  defaultCurrency,
  isAtHotelBrand,
  UserInfoContext,
} from "@hopper-b2b/utilities";
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useMemo,
} from "react";

export type UserSettingsContext = {
  preferredCurrency: Currency;
};

export const UserSettingsContext = createContext<UserSettingsContext>({
  preferredCurrency: Currency.USD,
});

type UserSettingsProviderProps = {
  userInfoContext: UserInfoContext;
};

export const UserSettingsProvider: FC<
  UserSettingsProviderProps & PropsWithChildren
> = ({ userInfoContext, children }) => {
  const userCurrency =
    userInfoContext?.state?.sessionInfo?.userInfo?.defaultCurrency;

  const shouldDefaultCurrency =
    !!useExperiment("WebHotelPreferredCurrencyDefaulting") && isAtHotelBrand();

  const preferredCurrency = useMemo(() => {
    const currency = defaultCurrency(shouldDefaultCurrency, userCurrency);
    return currency || (userCurrency as Currency) || Currency.USD;
  }, [shouldDefaultCurrency, userCurrency]);

  const userSettings = useMemo<UserSettingsContext>(
    () => ({ preferredCurrency }),
    [preferredCurrency]
  );

  return (
    <UserSettingsContext.Provider value={userSettings}>
      {children}
    </UserSettingsContext.Provider>
  );
};

export const useUserSettingsContext = () => {
  const ctx = useContext(UserSettingsContext);
  if (ctx === undefined)
    throw new Error(`must be used within a UserSettingsProvider`);

  return { ...ctx };
};
